<template>
  <div class="field-group">
    <label :for="id">{{ label }}</label>

    <div class="amount-row">
      <button
        class="currency-select"
        :class="{ 'is-error': !!errorMessage }"
        type="button"
        :disabled="disabled"
        @click="$emit('toggle-currency')"
        @blur="$emit('blur')"
      >
        <span>{{ displayCurrency }}</span>
        <span class="chevron">⌄</span>
      </button>

      <div class="amount-input-wrap" :class="{ 'is-error': !!errorMessage }">
        <input
          :id="id"
          :value="modelValue"
          type="text"
          inputmode="numeric"
          :placeholder="placeholder"
          :disabled="disabled"
          @input="$emit('update:modelValue', $event.target.value)"
          @blur="$emit('blur')"
        />
      </div>
    </div>

    <div v-if="currencyOpen" class="options-panel currency-options">
      <button
        v-for="option in currencies"
        :key="getCurrencyValue(option)"
        type="button"
        class="option-item"
        :class="{ active: currency === getCurrencyValue(option) }"
        @click="$emit('select-currency', getCurrencyValue(option))"
      >
        {{ getCurrencyLabel(option) }}
      </button>
    </div>

    <small v-if="errorMessage" class="error-text">{{ errorMessage }}</small>
    <small v-else-if="hint">{{ hint }}</small>
  </div>
</template>

<script>
export default {
  name: 'AmountInput',
  props: {
    id: {
      type: String,
      required: true,
    },
    modelValue: {
      type: [String, Number],
      default: '',
    },
    label: {
      type: String,
      default: 'Target amount',
    },
    placeholder: {
      type: String,
      default: '100,000',
    },
    hint: {
      type: String,
      default: '',
    },
    errorMessage: {
      type: String,
      default: '',
    },
    currency: {
      type: String,
      required: true,
    },
    currencies: {
      type: Array,
      default: () => [],
    },
    currencyOpen: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'toggle-currency', 'select-currency', 'blur'],
  computed: {
    displayCurrency() {
      const selected = this.currencies.find(
        (option) => this.getCurrencyValue(option) === this.currency,
      )
      return selected ? this.getCurrencyLabel(selected) : this.currency
    },
  },
  methods: {
    getCurrencyValue(option) {
      return typeof option === 'object'
        ? option.code ?? option.value ?? option.label
        : option
    },
    getCurrencyLabel(option) {
      return typeof option === 'object'
        ? option.label ?? option.code ?? option.value
        : option
    },
  },
}
</script>

<style scoped>
.field-group {
  display: flex;
  flex-direction: column;
}

.field-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #36442c;
}

.amount-row {
  display: grid;
  grid-template-columns: 104px 1fr;
  gap: 10px;
}

.currency-select {
  width: 100%;
  min-height: 58px;
  padding: 0 16px;
  border-radius: 16px;
  border: 1.5px solid #b8c2aa;
  background: #ffffff;
  font-size: 16px;
  color: #26311d;
  outline: none;
  box-shadow: 0 6px 14px rgba(68, 85, 38, 0.05);
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
}

.currency-select:focus {
  border-color: #fcc415;
  box-shadow: 0 0 0 4px rgba(252, 196, 21, 0.18),
    0 9px 18px rgba(68, 85, 38, 0.08);
}

.currency-select.is-error {
  border-color: #cf7d7d;
  box-shadow: 0 6px 14px rgba(207, 125, 125, 0.08);
}

.currency-select.is-error:focus {
  border-color: #cf7d7d;
  box-shadow: 0 0 0 4px rgba(207, 125, 125, 0.14),
    0 9px 18px rgba(207, 125, 125, 0.12);
}

.amount-input-wrap {
  border: 1.5px solid #b8c2aa;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 6px 14px rgba(68, 85, 38, 0.05);
  overflow: hidden;
}

.amount-input-wrap:focus-within {
  border-color: #fcc415;
  box-shadow: 0 0 0 4px rgba(252, 196, 21, 0.18),
    0 9px 18px rgba(68, 85, 38, 0.08);
}

.amount-input-wrap.is-error {
  border-color: #cf7d7d;
  box-shadow: 0 6px 14px rgba(207, 125, 125, 0.08);
}

.amount-input-wrap.is-error:focus-within {
  border-color: #cf7d7d;
  box-shadow: 0 0 0 4px rgba(207, 125, 125, 0.14),
    0 9px 18px rgba(207, 125, 125, 0.12);
}

.amount-input-wrap input {
  width: 100%;
  min-height: 58px;
  padding: 0 16px;
  border: 0;
  border-radius: 0;
  background: #ffffff;
  font-size: 16px;
  color: #26311d;
  outline: none;
  box-shadow: none !important;
}

.amount-input-wrap input::placeholder {
  color: #8b9485;
}

.chevron {
  font-size: 18px;
  color: #556245;
}

.options-panel {
  margin-top: 8px;
  border: 1px solid #cfd7c1;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 12px 26px rgba(34, 40, 24, 0.1);
  overflow: hidden;
}

.currency-options {
  max-width: 104px;
}

.option-item {
  width: 100%;
  border: 0;
  background: #ffffff;
  text-align: left;
  padding: 14px 16px;
  font-size: 15px;
  color: #26311d;
}

.option-item + .option-item {
  border-top: 1px solid #edf0e8;
}

.option-item.active {
  background: rgba(252, 196, 21, 0.14);
  color: #445526;
  font-weight: 700;
}

.field-group small {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  color: #5d6657;
}

.error-text {
  display: block;
  margin-top: 8px;
  color: #b05b5b !important;
  font-size: 12px;
  line-height: 1.5;
}
</style>
